"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const REASONS = [
  { key: "STOCK_ON_FIRE", label: "Stock on fire" },
  { key: "STOLEN_GOODS", label: "Stolen goods" },
  { key: "DAMAGED_GOODS", label: "Damaged goods" },
  { key: "STOCK_WRITTEN_OFF", label: "Stock written off" },
  { key: "STOCKTAKING_RESULTS", label: "Stocktaking results" },
  { key: "INVENTORY_REVALUATION", label: "Inventory revaluation" },
  { key: "OTHER", label: "Other" },
];

const RULES = [
  {
    topic: "Save is Draft — stock waits for Approve",
    rule: "Save does not change available qty or post a journal. Approve (Edit, Draft only) writes stock and the ledger. Reject sets Rejected and still does not touch stock. There is no Delete",
  },
  {
    topic: "Statuses",
    rule: "Draft, Approved, Rejected. Edit, Approve, and Reject only on Draft",
  },
  {
    topic: "Adjustment number",
    rule: "Auto-generated unless you turn that off under Configure Inventory Adjustment Number Preferences. Default prefix INA. Locked after save when auto-generate is on",
  },
  {
    topic: "Updated Stock is the new count",
    rule: "Current Stock is loaded from the warehouse and is not the signed qty you type. You enter Updated Stock (what should be on hand). Adjusted Stock = Updated − Current. Positive adds a batch; negative deducts FIFO. Purchase Price is required only when Adjusted Stock is greater than 0",
  },
  {
    topic: "Account",
    rule: "Required. Chart of Accounts picker limited to Expense, Other Expense, COGS, Income, Other Income. Approve also requires that account. Increase: Dr item Inventory account, Cr this account. Decrease: Dr this account, Cr item Inventory account. Item must have an inventory account",
  },
  {
    topic: "One pending per item per warehouse",
    rule: "Blocked if that item already has a Draft adjustment for the same warehouse: An inventory adjustment request (INA…) for item … is already pending for this warehouse",
  },
  {
    topic: "Tracked inventory items only",
    rule: "The item table and Add Bulk Item show Track inventory goods. Service items and Track inventory off never appear. Approve also refuses them",
  },
  {
    topic: "Insufficient stock on Approve",
    rule: "A negative line fails Approve if the warehouse no longer has enough qty. Nothing is half-posted",
  },
  {
    topic: "No Duplicate, no list Export, no GST, no attachments",
    rule: "Add Bulk Item can append several items at once. Division / Branch / Warehouse can pre-fill from the module's default configuration on the number drawer",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Creating an adjustment (Draft)" },
      { heading: "2. Lines — current, updated, adjusted, price" },
      { heading: "3. Reasons and the adjustment account" },
      { heading: "4. The list, search, and filters" },
      { heading: "5. Editing a Draft" },
      { heading: "6. Approving — stock and ledger" },
      { heading: "7. Rejecting" },
      { heading: "8. Detail and Stock Movement" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function InventoryAdjustmentPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Inventory Adjustment
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone correcting warehouse qty after a count, damage, or
            write-off — Approve posts it
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            An <B>Inventory Adjustment</B> is a Draft voucher. Prefix <B>INA</B>
            . You set what the count <B>should</B> be (Updated Stock).{" "}
            <B>Save</B> does not move stock. <B>Approve</B> updates View
            Inventory and posts a journal against the item&apos;s Inventory
            account and the income/expense account you picked.
          </p>
          <Callout type="note">
            Only items with <B>Track inventory</B> on can be adjusted.{" "}
            <B>Service</B> items and goods with Track inventory off do not
            appear on the item table or Add Bulk Item, and cannot be approved.
          </Callout>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Creating an adjustment (Draft)
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            List <B>Add</B> → Add Inventory Adjustment. Pick Division, Branch,
            Warehouse first — the rest of the form stays locked until those
            three are set. <B>Save</B> / <B>Cancel</B>.
          </p>
          <Definitions
            items={[
              {
                term: "Inventory Adjustment#",
                desc: (
                  <>
                    Required. Gear:{" "}
                    <B>Configure Inventory Adjustment Number Preferences</B>.
                    Default prefix <B>INA</B>.
                  </>
                ),
              },
              {
                term: "Date",
                desc: <>Required. Defaults to today.</>,
              },
              {
                term: "Reference Number",
                desc: <>Optional.</>,
              },
              {
                term: "Reason",
                desc: (
                  <>Required. One reason for the whole voucher, not per line.</>
                ),
              },
              {
                term: "Account",
                desc: <>Required. Income or expense family — see section 3.</>,
              },
              {
                term: "Description",
                desc: <>Optional.</>,
              },
            ]}
          />
          <Screenshot caption="Screenshot: Add Inventory Adjustment with Division, Warehouse, Adjustment#, Reason, Account, and Save." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Lines — current, updated, adjusted, price
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Item table plus <B>Add Bulk Item</B>. Duplicate items on the same
            voucher are blocked in the picker.
          </p>
          <Callout type="note">
            Only <B>Track inventory</B> items show here. <B>Service</B> items
            and goods with Track inventory off do not — the picker is filtered
            that way after Warehouse is set.
          </Callout>
          <Definitions
            items={[
              {
                term: "Current Stock",
                desc: (
                  <>
                    Loaded from that warehouse when you pick the item. Not typed
                    as a plus/minus qty.
                  </>
                ),
              },
              {
                term: "Updated Stock",
                desc: <>What should be on hand after the count. Required.</>,
              },
              {
                term: "Adjusted Stock",
                desc: (
                  <>
                    Calculated: Updated − Current. Green when positive, red when
                    negative. This is what Approve will add or deduct.
                  </>
                ),
              },
              {
                term: "Purchase Price",
                desc: (
                  <>
                    Defaults from the item&apos;s cost price. Required and must
                    be greater than 0 when Adjusted Stock is positive (new batch
                    value). Not required on a decrease — FIFO uses existing
                    batch cost.
                  </>
                ),
              },
            ]}
          />
          <Callout type="note">
            To write off 3 units when Current is 10, set Updated Stock to 7 — do
            not type −3 in Updated Stock.
          </Callout>
          <Screenshot caption="Screenshot: item lines with Current Stock, Updated Stock, Adjusted Stock, Purchase Price, and Add Bulk Item." />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Reasons and the adjustment account
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Reason is a fixed list (one per voucher):
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Reason on the picker
                  </th>
                </tr>
              </thead>
              <tbody>
                {REASONS.map((r, i) => (
                  <tr
                    key={r.key}
                    className={
                      i < REASONS.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.label}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Account is the P&amp;L side of the journal. The inventory side is
            each item&apos;s Inventory account on Item Master. Narration:{" "}
            Inventory Adjustment INA….
          </p>
          <Screenshot caption="Screenshot: Reason picker (Stocktaking results, Damaged goods, …) and Account (expense / income)." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. The list, search, and filters
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Title Inventory Adjustment with a count. Add. Search (number,
            reference, status, reason, item name, warehouse, approver). Column
            picker. Filters: Status, Division, Branch, Warehouse. Columns
            include Adjustment#, Date, Approved By, Reason, Account, Reference,
            Status, Description, Division, Branch, Warehouse. Action: View,
            Edit, Approve, Reject. Row click → detail. No export.
          </p>
          <Screenshot caption="Screenshot: Inventory Adjustment list with Draft / Approved / Rejected and Approve on a Draft row." />

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Editing a Draft
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            List or detail. Number stays locked. Same form. Approved or Rejected
            cannot be edited.
          </p>

          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Approving — stock and ledger
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            List confirm: Inventory Approve / Are you sure to approve this
            inventory? Detail has the same action. Status becomes Approved;
            Approved By and approval date are stored.
          </p>
          <Definitions
            items={[
              {
                term: "Positive Adjusted Stock",
                desc: (
                  <>
                    Adds qty at the warehouse, creates a batch (source Inventory
                    Adjustment), adds qty × purchase price to inventory value.
                  </>
                ),
              },
              {
                term: "Negative Adjusted Stock",
                desc: (
                  <>
                    Deducts FIFO batches. Fails if available qty is not enough.
                  </>
                ),
              },
              {
                term: "Journal",
                desc: (
                  <>
                    Increase: debit Inventory, credit the adjustment account.
                    Decrease: the reverse. Posts in the same transaction as the
                    stock change.
                  </>
                ),
              },
            ]}
          />
          <Callout type="warning">
            Opening stock when you go live is not this voucher unless you choose
            to use it that way. Batches can also arrive as Opening Stock /
            Existing Inventory from item setup — those are not Inventory
            Adjustment documents.
          </Callout>
          <Screenshot caption="Screenshot: Approve confirm on a Draft adjustment." />

          <h3
            id="section-0-sub-6"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            7. Rejecting
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Draft only. Status Rejected. Stock and ledger were never posted, so
            nothing is reversed. You can then raise a new Draft for that item
            and warehouse.
          </p>

          <h3
            id="section-0-sub-7"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            8. Detail and Stock Movement
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
            Title Inventory Adjustment Details. Overview: number, date, reason,
            reference, account, division, branch, warehouse, total items,
            description, Adjusted Items table (Current / Adjusted / Updated /
            Purchase Price). Tab <B>Stock Movement</B> after Approve. Sidebar:
            Edit, Approve, Reject while Draft.
          </p>
          <Screenshot caption="Screenshot: Inventory Adjustment Details with Adjusted Items and Stock Movement." />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/inventory/view-inventory"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              View Inventory <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Available qty and value change after Approve
            </p>
          </Link>
          <Link
            href="/documentation/books/all-master/item-master"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Item Master <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Track inventory, cost price, and the Inventory account the journal
              needs
            </p>
          </Link>
          <Link
            href="/documentation/books/accountant/chart-of-accounts"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Chart of Accounts <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Click the inventory or expense account after Approve to see the
              journal
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
